/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import './style/snackbar.scss';

import React, {CSSProperties, MouseEventHandler, ReactElement, useContext, useMemo} from 'react';
import classNames from 'classnames';
import {convertColor} from "../util/colorUtil";
import {ControlContext} from "../util/controlContext";
import {Snack} from "../util/interfaces";

interface Props {
    onClick: MouseEventHandler;
    snack: Snack | null;
    active: boolean;
    containerClass?: string;
    textClass?: string;
    backgroundColor?: string;
    textColor?: string;
}

export default function Snackbar(props: Props) {
    const context = useContext(ControlContext);
    const {textClass, containerClass, active, onClick, snack} = props;
    const backgroundStyle: CSSProperties = useMemo(() => {
        let backgroundColor: string = context.baseColor;
        if (snack?.config?.backgroundColor) backgroundColor = snack.config.backgroundColor;
        else if (props.backgroundColor) backgroundColor = props.backgroundColor;
        return {backgroundColor: convertColor(backgroundColor)};
    }, [props.backgroundColor, props.snack, context.baseColor])
    const textStyle: CSSProperties = useMemo(() => {
        let color: string = context.contrastColor;
        if (snack?.config?.textColor) color = snack.config.textColor;
        else if (props.textColor) color = props.textColor;
        return {color: convertColor(color)};
    }, [props.textColor, context.contrastColor, props.snack]);

    function renderText() {
        if (!snack) return null;
        const {text} = snack;
        let element: ReactElement | null = null;
        if (typeof text === 'string')
            element = <label className={classNames('snackbar-text', textClass)} style={textStyle}>{text}</label>;
        else if (text) {
            const className = classNames((text.props as any)?.className, 'snackbar-text', textClass);
            const style = {...(text.props as any)?.style, ...textStyle};
            element = React.cloneElement(text as any, {className, style});
        }
        return element;
    }

    return (
        <div className={classNames('snackbar', active && 'active', containerClass)} style={backgroundStyle}
             onClick={onClick}>
            {renderText()}
        </div>
    );
}